/* GenQuotes legal content — sourced verbatim from /app/memory legal docs (22 May 2026). */

export const TERMS_DOC = {
  slug: "terms",
  title: "Terms and Conditions",
  lastUpdated: "28 February 2026",
  intro:
    "IMPORTANT: These Terms and Conditions constitute a legally binding agreement between you and GenQuotes (ABN: 59487165324). Please read them carefully before using our services. By accessing or using our platform, you agree to be bound by these terms. If you do not agree, do not use our services.",
  sections: [
    {
      heading: "1. Definitions",
      lead: "In these Terms and Conditions, the following definitions apply:",
      bullets: [
        "\"GenQuotes\", \"we\", \"us\", or \"our\" refers to GenQuotes (ABN: 59487165324), the operator of the GenQuotes platform and services.",
        "\"Platform\" means the GenQuotes website, web application, and all associated software, tools, and services accessible at genquotes.com.",
        "\"Service\" means the automated quoting widget tool, subscription management system, and any related features provided by GenQuotes.",
        "\"Client\" or \"you\" means any individual or business entity that registers for, subscribes to, or otherwise uses the Service.",
        "\"End User\" means any third party who interacts with a Client's embedded quoting widget on the Client's website.",
        "\"Subscription\" means a paid or trial plan that grants access to the Service.",
        "\"Widget\" means the embeddable quoting tool configured by GenQuotes and deployed on a Client's website.",
        "\"Content\" means all data, text, information, and materials submitted to or generated by the Platform.",
      ],
    },
    {
      heading: "2. Acceptance of Terms",
      lead: "By registering for a GenQuotes account, starting a free trial, or using any part of the Service, you confirm that:",
      bullets: [
        "You have read and understood these Terms and Conditions;",
        "You are at least 18 years of age;",
        "You have the legal authority to enter into this agreement on behalf of yourself or the business entity you represent;",
        "You agree to comply with all applicable laws and regulations, including Australian Consumer Law.",
      ],
      body:
        "We reserve the right to update these Terms and Conditions at any time. We will notify you of material changes via email or a prominent notice on the Platform. Continued use of the Service after any such changes constitutes acceptance of the updated Terms.",
    },
    {
      heading: "3. Services Provided",
      lead:
        "GenQuotes provides a software-as-a-service (SaaS) platform that enables service businesses to offer automated, instant quoting functionality to their website visitors. The Service includes:",
      bullets: [
        "A configurable quoting widget that can be embedded on a Client's website via an iFrame or embed code;",
        "Automated price calculation based on Client-configured pricing rules;",
        "Collection of End User contact details upon quote acceptance;",
        "Email notification to the Client upon each accepted quote;",
        "An account management portal for subscription and configuration management.",
      ],
      body:
        "We reserve the right to modify, suspend, or discontinue any aspect of the Service at any time with reasonable notice to Clients. We will endeavour to provide at least 30 days' notice of material changes to the Service.",
      subSections: [
        {
          heading: "3.1 White-Label Service",
          body:
            "The Service is provided as a white-label solution. Clients may embed the Widget on their websites under their own business branding. End Users interacting with an embedded Widget may not be aware that the Widget is powered by GenQuotes technology. Clients are responsible for ensuring their use of the Widget complies with all applicable consumer protection and privacy laws including the Australian Privacy Act 1988.",
        },
      ],
    },
    {
      heading: "4. Account Registration and Security",
      lead:
        "To access the Service, you must create an account by providing accurate and complete information. You agree to:",
      bullets: [
        "Provide truthful, accurate, and complete registration information;",
        "Maintain and promptly update your account information;",
        "Keep your login credentials confidential and not share them with third parties;",
        "Notify us immediately at support@genquotes.com if you suspect any unauthorised access to your account;",
        "Accept responsibility for all activity that occurs under your account.",
      ],
      body:
        "We reserve the right to suspend or terminate accounts where we reasonably believe registration information is false, inaccurate, or misleading.",
    },
    {
      heading: "5. Free Trial",
      lead:
        "GenQuotes offers a 28-day free trial to new Clients. The following conditions apply to the free trial:",
      bullets: [
        "A valid payment method (credit or debit card) is required at the time of registration to commence the free trial;",
        "No charge will be made to your payment method during the 28-day trial period;",
        "The trial provides full access to all features included in the QuoteSnap Pro plan;",
        "At the conclusion of the 28-day trial period, your nominated payment method will automatically be charged the applicable monthly subscription fee unless you cancel before the trial end date;",
        "You will receive an email reminder 3 days before the end of the trial period;",
        "Only one free trial is permitted per business entity.",
      ],
      body:
        "GenQuotes reserves the right to refuse a free trial where we reasonably believe the offer is being misused.",
    },
    {
      heading: "6. Subscription, Billing and Payment",
      subSections: [
        {
          heading: "6.1 Subscription Plans",
          lead: "GenQuotes offers the following subscription plan:",
          body:
            "QuoteSnap Pro: $79.00 AUD per month, billed monthly. Pricing is subject to change with 30 days' written notice to existing subscribers. Price changes will not affect your current billing period.",
        },
        {
          heading: "6.2 Billing",
          lead:
            "By subscribing to the Service, you authorise GenQuotes to charge your nominated payment method on a recurring monthly basis. Billing occurs:",
          bullets: [
            "On the day following the conclusion of your free trial;",
            "On the same calendar date each subsequent month;",
            "Automatically, without further authorisation required for each billing cycle.",
          ],
          body:
            "All prices are stated in Australian Dollars (AUD). Where applicable, Goods and Services Tax (GST) will be applied in accordance with Australian tax law.",
        },
        {
          heading: "6.3 Payment Processing",
          body:
            "Payments are processed by Stripe, Inc., a third-party payment processor. By providing your payment details, you also agree to Stripe's terms of service. GenQuotes does not store your full card details on its servers.",
        },
        {
          heading: "6.4 Failed Payments",
          lead: "If a payment fails, GenQuotes will:",
          bullets: [
            "Retry the payment up to three times over a 7-day period;",
            "Send you an email notification requesting you update your payment details;",
            "Suspend access to the Service if payment cannot be collected after all retry attempts are exhausted.",
          ],
          body:
            "Your widget will remain active during the retry period. If the subscription is not reinstated within the retry period, the widget will be deactivated.",
        },
      ],
    },
    {
      heading: "7. Cancellation and Refunds",
      subSections: [
        {
          heading: "7.1 Cancellation by Client",
          lead: "You may cancel your subscription at any time by:",
          bullets: [
            "Accessing the \"Manage Subscription\" section in your account portal; or",
            "Contacting us at support@genquotes.com.",
          ],
          body:
            "Cancellation takes effect at the end of your current billing period. You will retain access to the Service until that date. No partial refunds are provided for unused time within a billing period.",
        },
        {
          heading: "7.2 Cancellation During Free Trial",
          body:
            "If you cancel during the 28-day free trial period, you will not be charged. Your widget will be deactivated at the time of cancellation.",
        },
        {
          heading: "7.3 Refunds",
          lead:
            "GenQuotes does not generally provide refunds for subscription fees already paid. However, we will consider refund requests on a case-by-case basis where:",
          bullets: [
            "A technical fault attributable to GenQuotes prevented you from using the Service for a significant period; or",
            "You were charged in error.",
          ],
          body:
            "Refund requests must be submitted to support@genquotes.com within 14 days of the charge in question. This clause does not limit any rights you may have under the Australian Consumer Law.",
        },
        {
          heading: "7.4 Cancellation by GenQuotes",
          lead:
            "GenQuotes reserves the right to suspend or terminate your account and subscription immediately if you:",
          bullets: [
            "Breach any provision of these Terms and Conditions;",
            "Use the Service in a manner that is unlawful, fraudulent, or harmful;",
            "Fail to pay applicable subscription fees after the retry period.",
          ],
          body:
            "Where reasonably practicable, we will provide 14 days' notice of termination, except in cases of serious breach or fraud.",
        },
      ],
    },
    {
      heading: "8. Client Obligations and Acceptable Use",
      lead: "As a Client, you agree to:",
      bullets: [
        "Use the Service only for lawful purposes and in accordance with these Terms;",
        "Ensure that the pricing information and business details you configure in your Widget are accurate and not misleading;",
        "Comply with all applicable laws when collecting End User data through your Widget, including obligations under the Australian Privacy Act 1988;",
        "Not resell, sublicense, or otherwise transfer access to the Service to any third party without our written consent;",
        "Not attempt to reverse engineer, decompile, or otherwise access the source code of the Platform;",
        "Not use the Service in a manner that could damage, overburden, or impair our infrastructure;",
        "Not use the Service to transmit unsolicited communications, malware, or any content that is unlawful, defamatory, or harmful.",
      ],
    },
    {
      heading: "9. Intellectual Property",
      body:
        "All intellectual property in the GenQuotes Platform, including software, design, trademarks, and documentation, is owned by or licensed to GenQuotes. Nothing in these Terms transfers any intellectual property rights to you. You retain ownership of any business data, pricing information, and branding you configure within the Platform. You grant GenQuotes a limited, non-exclusive licence to use your data solely to provide the Service to you. You must not use the GenQuotes name, logo, or trademarks without our prior written consent.",
    },
    {
      heading: "10. Data and Privacy",
      body:
        "GenQuotes collects, stores, and processes personal information in accordance with our Privacy Policy, which forms part of these Terms and Conditions. By using the Service, you agree to the terms of our Privacy Policy. You acknowledge that as a Client, you are responsible for ensuring that your use of the Widget to collect End User data complies with all applicable privacy laws, including the Australian Privacy Act 1988 and the Australian Privacy Principles. You agree to maintain a compliant privacy policy on your own website that discloses to End Users how their data is collected and used through your Widget.",
    },
    {
      heading: "11. Limitation of Liability",
      lead: "To the maximum extent permitted by law:",
      bullets: [
        "The Service is provided \"as is\" and \"as available\" without warranty of any kind, express or implied;",
        "GenQuotes does not warrant that the Service will be uninterrupted, error-free, or completely secure;",
        "GenQuotes is not responsible for the accuracy of quotes generated by your Widget — it is your responsibility to ensure your pricing configuration is correct;",
        "GenQuotes' total liability to you for any claim arising from your use of the Service is limited to the total amount you paid for the Service in the three months preceding the claim;",
        "GenQuotes is not liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or business opportunities.",
      ],
      body:
        "Nothing in these Terms limits liability that cannot be excluded under Australian Consumer Law, including consumer guarantees under the Competition and Consumer Act 2010 (Cth).",
    },
    {
      heading: "12. Indemnification",
      lead:
        "You agree to indemnify and hold harmless GenQuotes, its directors, employees, and agents from any claims, losses, damages, liabilities, and expenses (including legal fees) arising from:",
      bullets: [
        "Your use of the Service in breach of these Terms;",
        "Your breach of any applicable law or third-party rights;",
        "Any inaccurate, misleading, or unlawful content you configure in your Widget;",
        "Any claim by an End User arising from their use of your Widget.",
      ],
    },
    {
      heading: "13. Third-Party Services",
      body:
        "The Service integrates with third-party services including Stripe (payment processing), Resend (email delivery), and Supabase (data storage). GenQuotes is not responsible for the availability, performance, or terms of these third-party services. Your use of these services is subject to their respective terms and privacy policies.",
    },
    {
      heading: "14. Referral Services",
      body:
        "GenQuotes may refer clients to third party service providers including web design and AI consultancy services. GenQuotes may receive a referral fee for such introductions. Referrals are made at GenQuotes' discretion and GenQuotes accepts no liability for the quality, timeliness, or outcome of services provided by any referred third party.",
    },
    {
      heading: "15. Governing Law and Disputes",
      body:
        "These Terms and Conditions are governed by the laws of South Australia, Australia. Any dispute arising under these Terms shall be subject to the exclusive jurisdiction of the courts of South Australia. Before commencing legal proceedings, both parties agree to attempt to resolve any dispute through good-faith negotiation for a period of 30 days.",
    },
    {
      heading: "16. General Provisions",
      bullets: [
        "Entire Agreement: These Terms, together with our Privacy Policy, constitute the entire agreement between you and GenQuotes regarding the Service.",
        "Severability: If any provision of these Terms is found to be unenforceable, the remaining provisions continue in full force.",
        "Waiver: Failure to enforce any provision does not constitute a waiver of that provision.",
        "Assignment: You may not assign your rights under these Terms without our written consent. We may assign our rights and obligations to a successor entity.",
        "Notices: Notices to GenQuotes should be sent to support@genquotes.com. Notices to you will be sent to the email address on your account.",
      ],
    },
    {
      heading: "17. Contact",
      lead: "For questions about these Terms and Conditions, please contact:",
      body: "GenQuotes (ABN: 59487165324)\nEmail: support@genquotes.com\nWebsite: genquotes.com",
    },
  ],
  footer:
    "Document version: 28 February 2026 — This document is a draft for legal review only and does not constitute legal advice.",
};

export const PRIVACY_DOC = {
  slug: "privacy",
  title: "Privacy Policy",
  lastUpdated: "22 May 2026",
  intro:
    "GenQuotes (ABN: 59487165324) is committed to protecting the privacy of our clients and their customers. This Privacy Policy explains how we collect, use, store, and disclose personal information in accordance with the Australian Privacy Act 1988 (Cth) and the Australian Privacy Principles (APPs).",
  sections: [
    {
      heading: "1. About This Policy",
      lead: "This Privacy Policy applies to:",
      bullets: [
        "GenQuotes clients (\"Clients\") — businesses that subscribe to and use our Platform;",
        "End Users — customers of our Clients who interact with an embedded GenQuotes quoting widget;",
        "Visitors to the GenQuotes website at genquotes.com.",
      ],
      body:
        "By using our Platform or website, you agree to the collection and use of information as described in this Policy. If you are an End User interacting with a Client's widget, we recommend also reviewing the privacy policy of the business whose website you are visiting.",
    },
    {
      heading: "2. What Personal Information We Collect",
      subSections: [
        {
          heading: "2.1 From Clients (Business Subscribers)",
          lead: "When you register and use GenQuotes, we collect:",
          bullets: [
            "Identity information: full name, business name;",
            "Contact information: email address, phone number (if provided);",
            "Account credentials: email address and hashed password;",
            "Billing information: payment card details (processed and stored by Stripe — we do not store full card numbers);",
            "Business configuration data: service types, pricing rules, branding preferences, and notification email addresses you enter into the Platform;",
            "Usage data: widget activity, quote volumes, login history, and account interactions.",
          ],
        },
        {
          heading: "2.2 From End Users (Via Client Widgets)",
          lead:
            "When an End User accepts a quote through a Client's embedded widget, we collect on behalf of the Client:",
          bullets: [
            "Full name;",
            "Email address;",
            "Phone number (where provided).",
          ],
          body:
            "This data is collected solely to facilitate the service enquiry between the End User and the Client. GenQuotes acts as a data processor in respect of End User data — the Client is the data controller responsible for ensuring collection is lawful and disclosed in their own privacy policy.",
        },
        {
          heading: "2.3 From Website Visitors",
          lead: "When you visit genquotes.com, we may automatically collect:",
          bullets: [
            "IP address and approximate location;",
            "Browser type and version;",
            "Pages viewed and time spent on the site;",
            "Referring URL.",
          ],
          body:
            "This information is collected via cookies and similar technologies. See Section 8 for more information on our use of cookies.",
        },
      ],
    },
    {
      heading: "3. How We Use Personal Information",
      lead: "GenQuotes uses personal information for the following purposes:",
      bullets: [
        "To provide, operate, and improve the Service;",
        "To process payments and manage subscriptions;",
        "To send transactional emails including welcome messages, quote notifications, billing receipts, and service communications;",
        "To send trial expiry reminders and subscription renewal notices;",
        "To respond to support enquiries and resolve technical issues;",
        "To detect and prevent fraud, abuse, and security incidents;",
        "To comply with legal obligations;",
        "To analyse usage patterns and improve the Platform.",
      ],
      body:
        "We do not use personal information for direct marketing without your consent. We do not sell personal information to third parties under any circumstances.",
    },
    {
      heading: "4. Legal Basis for Processing",
      lead:
        "GenQuotes collects and processes personal information on the following grounds:",
      bullets: [
        "Contract performance: to fulfil our obligations under the subscription agreement with Clients;",
        "Legitimate interests: to operate, maintain, and improve our Platform, detect fraud, and ensure security;",
        "Legal obligation: to comply with applicable Australian laws and regulations;",
        "Consent: where you have provided explicit consent for a specific purpose (e.g. marketing communications).",
      ],
    },
    {
      heading: "5. Disclosure of Personal Information",
      lead:
        "GenQuotes may disclose personal information to the following categories of third parties:",
      subSections: [
        {
          heading: "5.1 Service Providers",
          bullets: [
            "Stripe, Inc. — payment processing. Stripe processes billing information on our behalf. Subject to Stripe's Privacy Policy at stripe.com/privacy.",
            "Resend — transactional email delivery. Resend processes email addresses to deliver service communications. Subject to Resend's Privacy Policy.",
            "Supabase — cloud database and authentication services. Personal data is stored in Supabase infrastructure. Subject to Supabase's Privacy Policy.",
          ],
        },
        {
          heading: "5.2 Other Disclosures",
          lead: "We may also disclose personal information:",
          bullets: [
            "To law enforcement, regulators, or courts where required by law or legal process;",
            "To protect the rights, property, or safety of GenQuotes, our Clients, End Users, or the public;",
            "In connection with a business sale, merger, or acquisition — in such circumstances, we will notify affected individuals.",
          ],
          body:
            "We do not disclose personal information to overseas recipients except where our third-party service providers operate internationally (including Stripe and Supabase, which may store data in the United States). We take reasonable steps to ensure these providers maintain appropriate data protection standards.",
        },
      ],
    },
    {
      heading: "6. Data Storage and Security",
      lead:
        "GenQuotes takes reasonable steps to protect personal information from misuse, interference, loss, unauthorised access, modification, or disclosure. Our security measures include:",
      bullets: [
        "Encryption of data in transit using TLS/HTTPS;",
        "Encrypted storage of passwords using industry-standard hashing;",
        "Row-level security controls in our database to ensure Client data isolation;",
        "Access controls limiting data access to authorised personnel only;",
        "Regular security reviews of our Platform and infrastructure.",
      ],
      body:
        "While we take reasonable precautions, no method of electronic storage or transmission is 100% secure. We cannot guarantee absolute security of personal information.\nPersonal information is retained for as long as necessary to provide the Service. Upon cancellation of a subscription, Client data is retained for 90 days to allow for reactivation, then deleted or anonymised. End User quote data may be retained for up to 12 months for service continuity purposes.",
    },
    {
      heading:
        "7. Your Privacy Rights Under the Australian Privacy Act 1988 and the Australian Privacy Principles",
      lead: "You have the following rights:",
      subSections: [
        {
          heading: "7.1 Access",
          body:
            "You have the right to request access to the personal information we hold about you. To make an access request, contact us at privacy@genquotes.com. We will respond within 30 days.",
        },
        {
          heading: "7.2 Correction",
          body:
            "If you believe personal information we hold about you is inaccurate, incomplete, or out of date, you may request that we correct it. We will respond to correction requests within 30 days.",
        },
        {
          heading: "7.3 Deletion",
          body:
            "You may request deletion of your personal information by contacting us at privacy@genquotes.com. We will delete your data subject to any legal obligations we have to retain it.",
        },
        {
          heading: "7.4 Complaints",
          body:
            "If you believe we have mishandled your personal information, you may lodge a complaint with us at privacy@genquotes.com. We will acknowledge your complaint within 5 business days and respond substantively within 30 days. If you are not satisfied with our response, you may lodge a complaint with the Office of the Australian Information Commissioner (OAIC) at oaic.gov.au or by calling 1300 363 992.",
        },
      ],
    },
    {
      heading: "8. Cookies",
      lead:
        "GenQuotes uses cookies and similar tracking technologies on our website for the following purposes:",
      bullets: [
        "Essential cookies: required for the Platform to function, including maintaining login sessions and security tokens;",
        "Analytics cookies: to understand how visitors use our website and improve our services;",
        "Preference cookies: to remember your settings and preferences.",
      ],
      body:
        "You can control cookie settings through your browser. Disabling essential cookies may prevent you from using parts of the Service. We display a cookie consent notice on your first visit to our website. We do not use cookies for advertising or cross-site tracking purposes.",
    },
    {
      heading: "9. Children's Privacy",
      body:
        "The GenQuotes Service is intended for use by business owners and is not directed at individuals under the age of 18. We do not knowingly collect personal information from minors. If we become aware that we have inadvertently collected such information, we will delete it promptly.",
    },
    {
      heading: "10. Changes to This Policy",
      body:
        "We may update this Privacy Policy from time to time. We will notify you of material changes by email or by a prominent notice on our website. Your continued use of the Service after any changes constitutes acceptance of the updated Policy. We encourage you to review this Policy periodically.",
    },
    {
      heading: "11. Contact Us",
      lead:
        "For any privacy-related enquiries, requests, or complaints, please contact:",
      body:
        "GenQuotes (ABN: 59487165324) — Privacy Officer\nEmail: privacy@genquotes.com\nWebsite: genquotes.com\n\nWe aim to respond to all privacy enquiries within 5 business days.",
    },
  ],
  footer:
    "Document version: 22 May 2026 — This document is a draft for legal review only and does not constitute legal advice. GenQuotes recommends this document be reviewed by a qualified Australian solicitor prior to publication.",
};
